import { useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import isTestUser from "src/constants/isTestUser";
import useDim from "src/hooks/useDim";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";
import { paddingXL } from "src/styles/spacing";
import type { ImageProps } from "src/swsh-native";
import { Timeout } from "src/types/time";
import FloatingBackgroundImage from "./components/FloatingBackgroundImage";
import { FLOATING_PHOTO_HEIGHT, FLOATING_PHOTO_WIDTH } from "./constants";
import { FloatingPhoto } from "./types";

const INTERVAL_TIME = 500;
const DURATION = 6000;
const EXTRA_OVERFLOW_MARGIN = 400;
const COLLISION_MARGIN = paddingXL;
const NEARLY_EXPIRED_CUTOFF = 100;
const DISABLED = !!isTestUser;

const FloatingBackgroundImages = ({
	photos,
	dim: dimProp,
	photoDensity = 1,
}: {
	photos: { src: ImageProps["src"] | null | undefined }[];
	/**
	 * Dimensions of the container. By default, full screen is used
	 */
	dim?: { width: number; height: number };
	/**
	 * @default 1
	 *
	 * Controls the density of the floating photos. They'll still avoid overlapping, but higher density changes the likelihood that there are more photos
	 */
	photoDensity?: number;
}) => {
	const [floatingPhotos, setFloatingPhotos] = useState<FloatingPhoto[]>([]);

	const windowDim = useDim();
	const dim = useMemo(() => dimProp ?? windowDim, [dimProp, windowDim]);
	const count = useRef(0);
	const addFloatingPhotos = useStabilizedCallback(
		({
			delayOffset = 0,
			delayMultiplier = 1,
		}: { delayOffset?: number; delayMultiplier?: number } = {}) => {
			setFloatingPhotos((prev) => {
				const cur = getCurTime_MS();
				const area = dim.width * dim.height;
				const goalNumImages =
					Math.max(Math.min(6, Math.floor(0.00001 * area) + 1), 3) * photoDensity;

				let numNearlyExpired = 0;
				const newPhotos: FloatingPhoto[] = [];
				for (const p of prev) {
					const expiration = p.createdAt + p.delay + p.duration;
					if (expiration > cur) {
						newPhotos.push(p);
						if (expiration - cur < NEARLY_EXPIRED_CUTOFF) {
							numNearlyExpired++;
						}
					}
				}
				const candidatePhotos = photos.filter((photo) => photo.src) as {
					src: ImageProps["src"];
				}[];

				/**
				 * Gets some { x, y } that doesn't overlap with anything in newPhotos
				 */
				const getRandomNonCollidingCoords = ({
					width,
					height,
					distance,
				}: {
					width: number;
					height: number;
					distance: number;
				}) => {
					const heightEffective = height + distance;
					for (let i = 0; i < 10; i++) {
						const x = Math.floor(Math.random() * (dim.width - width));
						const y = Math.floor(
							// Doing a weird height shift since we want to allow overflow in the y direction
							Math.random() * dim.height - 0.25 * heightEffective,
						);
						const collides = newPhotos.some((p) => {
							const pHeightEffective = p.height + distance;
							const pWidthEffective = p.width;
							const xCollides =
								x + width + COLLISION_MARGIN > p.x &&
								p.x + pWidthEffective + COLLISION_MARGIN > x;
							const yCollides =
								y + heightEffective + COLLISION_MARGIN > p.y &&
								p.y + pHeightEffective + COLLISION_MARGIN > y;
							return xCollides && yCollides;
						});
						if (!collides) {
							return { x, y };
						}
					}
					return null;
				};

				const addPhoto = (i: number) => {
					const sizeFactor = Math.random() * 0.3 + 0.7;
					const url = candidatePhotos[i % candidatePhotos.length]?.src;
					if (url) {
						const width = FLOATING_PHOTO_WIDTH * sizeFactor;
						const height = FLOATING_PHOTO_HEIGHT * sizeFactor;
						const distance = 50 + Math.random() * 50;
						const duration = DURATION + 0.2 * (Math.random() - 0.5) * DURATION;
						const coords = getRandomNonCollidingCoords({
							width,
							height,
							distance,
						});
						if (coords) {
							newPhotos.push({
								key: `${count.current++}`,
								createdAt: cur,
								delay:
									delayMultiplier * (INTERVAL_TIME / 2) * Math.random() +
									delayOffset,
								duration,
								...coords,
								distance,
								width,
								height,
								src: url,
							});
						}
					}
				};

				const startIndex = Math.floor(Math.random() * candidatePhotos.length);
				for (let i = startIndex; i < startIndex + 3; i++) {
					const odds =
						1 - (Math.max(newPhotos.length - numNearlyExpired, 0) / goalNumImages) ** 2;
					if (Math.random() < odds) {
						addPhoto(i);
					}
				}
				return newPhotos;
			});
		},
		[dim, photoDensity, photos],
	);

	const interval = useRef<Timeout | null>(null);
	useEffect(() => {
		if (photos.length > 0 && interval.current == null) {
			addFloatingPhotos({
				delayMultiplier: 0.25,
			});
			interval.current = setInterval(() => {
				addFloatingPhotos();
			}, INTERVAL_TIME);
		}
	}, [addFloatingPhotos, photos.length]);
	useEffect(() => {
		return () => {
			if (interval.current) clearInterval(interval.current);
		};
	}, []);

	if (DISABLED) return null;
	return (
		// Overflow-x and overflow-y are kinda useless for this situation, so this is a hack to only allow overflow in the y direction
		<View
			style={{
				position: "absolute",
				width: dim.width,
				height: dim.height + EXTRA_OVERFLOW_MARGIN,
				top: -EXTRA_OVERFLOW_MARGIN / 2,
				overflow: "hidden",
				zIndex: -1,
			}}
		>
			{floatingPhotos.map((p) => (
				<FloatingBackgroundImage
					key={p.key}
					floatingPhoto={p}
					yOffset={EXTRA_OVERFLOW_MARGIN / 2}
				/>
			))}
		</View>
	);
};
export default FloatingBackgroundImages;
